import React, { type ReactNode } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import LightMode from "@mui/icons-material/LightMode";
import DarkMode from "@mui/icons-material/DarkMode";
import { alpha, useTheme, useColorScheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useThemeSwitcher } from "../../theme";

export interface AuthPageProps {
  /** Rendered as the card's main heading (e.g. "Backoffice", or the reseller app name). */
  appTitle: string;
  logo: string;
  children: ReactNode;
}

/** Shared chrome for all auth screens: gradient background, brand logo, title, and a light/dark toggle. */
export function AuthPage({ appTitle, logo, children }: AuthPageProps) {
  const { t } = useTranslation();
  const { currentBrand } = useThemeSwitcher();
  const muiTheme = useTheme();
  const { mode, systemMode, setMode } = useColorScheme();
  const resolvedMode = mode === "system" ? systemMode : mode;
  const textColor = muiTheme.palette.text.primary;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 100%)",
        bgcolor: "background.default",
        position: "relative",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          p: 0.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <ToggleButtonGroup
          value={resolvedMode ?? "light"}
          exclusive
          onChange={(_, value) => value && setMode(value)}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              borderColor: alpha(textColor, 0.25),
              py: 0.25,
              px: 0.75,
            },
          }}
        >
          <ToggleButton value="light" aria-label={t("common.themeLight")}>
            <LightMode fontSize="small" />
          </ToggleButton>
          <ToggleButton value="dark" aria-label={t("common.themeDark")}>
            <DarkMode fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            padding: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: currentBrand === "gastrosoft" ? 30 : 60,
              mb: 3,
              objectFit: "contain",
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 700,
              color: "text.primary",
              textAlign: "center",
            }}
          >
            {appTitle}
          </Typography>
          {children}
        </Paper>
      </Container>
    </Box>
  );
}
